import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './onboarding.module.css';
import TourCard from './TourCard';
import STEPS, { ANCHORS, isStepAvailable, selectorFor } from './onboardingSteps';
import { computeCardPosition } from './tourPosition';
import { getLocale } from './locales';
import { ONBOARDING_EVENTS, trackOnboarding } from './onboardingAnalytics';
import { useOnboarding } from './onboardingContext';
import alert from '../../helpers/alert';

// Сам тур: затемняет экран, подсвечивает нужный элемент и ведёт по шагам.
//
// Библиотеку не берём намеренно. От неё нужны ровно две вещи — вырез в
// затемнении и выбор стороны для карточки; обе лежат ниже и занимают пару
// десятков строк. Зато карточка своя (два бренда, свои цвета и шрифты), нет
// новой зависимости в сборке, которая собирается на node 14, и шаг с
// пропавшим элементом мы пропускаем по своим правилам.

const CARD_FALLBACK = { width: 320, height: 180 };

// Сколько ждём, пока открывшийся раздел нарисует нужный элемент: двадцать
// попыток по 100 мс — две секунды. Не дождались — показываем подсказку без
// подсветки, чтобы тур не вставал.
const WAIT_ATTEMPTS = 20;
const WAIT_STEP_MS = 100;

// Меню ждём дольше: оно появляется после двух запросов к серверу, а на
// медленной связи это несколько секунд. Не дождались — показываем то, что
// есть, лишь бы тур не висел.
const MENU_WAIT_ATTEMPTS = 50;

const OnboardingTour = () => {
  const { tourRunning, finishTour } = useOnboarding();
  const locale = getLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardSize, setCardSize] = useState(CARD_FALLBACK);

  const cardRef = useRef(null);
  // Откуда человек начал обучение: в конце вернём его на ту же страницу.
  const startPath = useRef(null);

  // Список шагов фиксируем на старте: иначе счётчик «3 из 9» прыгал бы,
  // когда на странице появляется или исчезает элемент.
  //
  // Но сначала дожидаемся меню: оно рисуется после ответа сервера со
  // счётчиками, и тур, запущенный сразу после загрузки страницы, не находил
  // ни одного пункта — у новичка оставалось пять шагов вместо четырнадцати.
  useEffect(() => {
    if (!tourRunning) {
      setSteps([]);
      setIndex(0);
      return undefined;
    }

    startPath.current = location.pathname;
    setIndex(0);

    let attempts = 0;
    const timers = [];

    const fixSteps = () => {
      const menuReady = !!document.querySelector(selectorFor(ANCHORS.menuClients));

      if (!menuReady && attempts < MENU_WAIT_ATTEMPTS) {
        attempts += 1;
        timers.push(setTimeout(fixSteps, WAIT_STEP_MS));
        return;
      }

      setSteps(STEPS.filter((step) => isStepAvailable(step)));
    };

    fixSteps();

    return () => timers.forEach(clearTimeout);
    // location в зависимостях не нужен: путь запоминаем один раз, на старте.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRunning]);

  const step = steps[index];

  // Следующий шаг, элемент которого есть на странице. Шаг, который успел
  // пропасть (закрыли меню, ушли со страницы), просто перешагиваем.
  const findAvailable = useCallback(
    (from, direction) => {
      for (let i = from; i >= 0 && i < steps.length; i += direction) {
        if (isStepAvailable(steps[i])) return i;
      }
      return -1;
    },
    [steps]
  );

  // Тур водит человека по разделам, поэтому в конце возвращаем его туда,
  // откуда он начал, — иначе он останется на чужой странице.
  const endTour = useCallback(
    (outcome, stepId, stepNumber) => {
      const back = startPath.current;
      startPath.current = null;

      // Сбрасываем шаги до возврата: иначе последний шаг снова утащит нас
      // в свой раздел, и человек вернётся не туда, откуда начал.
      setSteps([]);
      setIndex(0);

      finishTour(outcome, stepId, stepNumber);

      // Пропустил — скажем, где его найти: иначе новичок решит, что обучение
      // показывают один раз и больше никогда.
      if (outcome === 'skipped') alert.success(locale.ui.whereToFind);

      if (back) navigate(back);
    },
    [finishTour, navigate, locale]
  );

  const goTo = useCallback(
    (from, direction) => {
      const next = findAvailable(from, direction);

      if (next === -1) {
        endTour('finished', step?.id, index + 1);
        return;
      }

      setIndex(next);
    },
    [findAvailable, endTour, step, index]
  );

  const handleNext = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const handleBack = useCallback(() => goTo(index - 1, -1), [goTo, index]);
  const handleSkip = useCallback(() => endTour('skipped', step?.id, index + 1), [endTour, step, index]);

  // Шаг про раздел открывает сам раздел: менеджер смотрит на свои живые
  // клиенты и сделки, а не слушает рассказ с чужой страницы.
  useEffect(() => {
    if (!tourRunning || !step?.route) return;
    if (location.pathname === step.route) return;

    navigate(step.route);
  }, [tourRunning, step, location.pathname, navigate]);

  // Положение подсвеченного элемента. Пересчитываем при смене шага, прокрутке
  // и изменении размера окна — иначе вырез уезжает от элемента.
  useLayoutEffect(() => {
    if (!tourRunning || !step) return undefined;

    if (!step.anchor) {
      setRect(null);
      return undefined;
    }

    let node = null;
    let attempts = 0;
    const timers = [];

    const update = () => {
      if (node) setRect(node.getBoundingClientRect());
    };

    // Раздел только что открылся и ещё рисуется — элемента пока нет. Ждём его
    // недолго, вместо того чтобы показывать подсказку в пустоту.
    const find = () => {
      node = document.querySelector(selectorFor(step.anchor));

      if (!node) {
        attempts += 1;
        if (attempts <= WAIT_ATTEMPTS) {
          timers.push(setTimeout(find, WAIT_STEP_MS));
        } else {
          setRect(null);
        }
        return;
      }

      update();

      if (typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        // После плавной прокрутки координаты меняются — обновим ещё раз.
        timers.push(setTimeout(update, 350));
      }
    };

    find();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [tourRunning, step, location.pathname]);

  // Реальный размер карточки — чтобы она не вылезала за край экрана.
  useLayoutEffect(() => {
    if (!cardRef.current) return;

    const { width, height } = cardRef.current.getBoundingClientRect();
    if (width && height) setCardSize({ width, height });
  }, [step, rect]);

  // Клавиатура: Esc закрывает, стрелки листают.
  useEffect(() => {
    if (!tourRunning) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleSkip();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleBack();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [tourRunning, handleNext, handleBack, handleSkip]);

  useEffect(() => {
    if (!tourRunning || !step) return;

    trackOnboarding(ONBOARDING_EVENTS.tourStepShown, {
      stepId: step.id,
      stepNumber: index + 1,
      total: steps.length,
    });
  }, [tourRunning, step, index, steps.length]);

  const position = useMemo(() => {
    if (!rect) return null;

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    return computeCardPosition(rect, cardSize, viewport, step?.placement);
  }, [rect, cardSize, step]);

  if (!tourRunning || !step) return null;

  const text = locale.steps[step.textKey] || { title: '', text: '' };
  const centered = !rect;

  return (
    <div className={styles.layer} data-testid="onboarding-tour">
      {rect ? (
        <div
          className={styles.hole}
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
        />
      ) : (
        <div className={styles.plainBackdrop} />
      )}

      <TourCard
        ref={cardRef}
        title={text.title}
        text={text.text}
        stepNumber={index + 1}
        total={steps.length}
        ui={locale.ui}
        position={position}
        centered={centered}
        isFirst={index === 0}
        isLast={index === steps.length - 1}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
      />
    </div>
  );
};

export default OnboardingTour;
